import {
  collection,
  documentId,
  getDocs,
  limit,
  orderBy,
  query,
  startAfter,
  where,
} from 'firebase/firestore';
import { db } from '../firebase';

const PAGE_SIZE = 500;

/** Numeric suffix from project IDs like LL001, LL1000, ET042. */
export function parseProjectIdNumber(projectId) {
  const match = String(projectId || '').match(/(\d+)$/);
  return match ? parseInt(match[1], 10) : 0;
}

/** Prefix before trailing digits, e.g. LL from LL1000. */
export function parseProjectIdPrefix(projectId) {
  const str = String(projectId || '');
  const match = str.match(/^([^\d]*)(\d+)$/);
  return match ? match[1] : str;
}

/** Sort project IDs numerically (LL1000 after LL999, not before LL100). */
export function compareProjectIds(a, b) {
  const prefixA = parseProjectIdPrefix(a);
  const prefixB = parseProjectIdPrefix(b);
  if (prefixA !== prefixB) return prefixA.localeCompare(prefixB);
  return parseProjectIdNumber(a) - parseProjectIdNumber(b);
}

function sortByCreatedOnDesc(quotes) {
  return [...quotes].sort((a, b) => {
    const aTime = a.createdOn?.toDate?.() ? a.createdOn.toDate().getTime() : 0;
    const bTime = b.createdOn?.toDate?.() ? b.createdOn.toDate().getTime() : 0;
    return bTime - aTime;
  });
}

/** Fetch every document in a collection, paging past Firestore batch limits. */
export async function fetchAllCollectionDocs(collRef, extraConstraints = []) {
  const allDocs = [];
  let lastDoc = null;

  while (true) {
    const constraints = [...extraConstraints, orderBy(documentId()), limit(PAGE_SIZE)];
    if (lastDoc) constraints.push(startAfter(lastDoc));

    const snap = await getDocs(query(collRef, ...constraints));
    if (snap.empty) break;

    allDocs.push(...snap.docs);
    if (snap.docs.length < PAGE_SIZE) break;
    lastDoc = snap.docs[snap.docs.length - 1];
  }

  return allDocs;
}

export async function fetchQuotesForUser({ currentUserUid, userRole }) {
  const coll = collection(db, 'QuoteRequirements');
  const isAdmin = userRole === 'PackagingAdmin' || userRole === 'ShippingAdmin';

  const constraints = isAdmin
    ? []
    : [where('createdBy', '==', currentUserUid)];

  const docs = await fetchAllCollectionDocs(coll, constraints);
  const quotes = docs.map((entry) => ({ id: entry.id, ...entry.data() }));
  return sortByCreatedOnDesc(quotes);
}

export function userCanAccessQuote(quote, { currentUserUid, userRole }) {
  if (!quote || !currentUserUid) return false;
  const isAdmin = userRole === 'PackagingAdmin' || userRole === 'ShippingAdmin';
  if (isAdmin) return true;
  return quote.createdBy === currentUserUid;
}
